import React, { useEffect, useRef } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';

const StripeApplePay = ({
  clientSecret,
  userData,
  totalAmount,
  redirect,
  updateIntentHandle,
  onPaymentConfirmed,
  onError,
  onProcessingChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const prButtonRef = useRef(null);
  const effectRunIdRef = useRef(0);
  const updateIntentRef = useRef(updateIntentHandle);
  const userDataRef = useRef(userData);
  const onPaymentConfirmedRef = useRef(onPaymentConfirmed);
  const redirectRef = useRef(redirect);
  const onErrorRef = useRef(onError);
  const onProcessingChangeRef = useRef(onProcessingChange);

  // Keep payment-time data current without rebuilding the Apple Pay button or
  // updating the intent on every personal-information keystroke.
  useEffect(() => {
    updateIntentRef.current = updateIntentHandle;
    userDataRef.current = userData;
    onPaymentConfirmedRef.current = onPaymentConfirmed;
    redirectRef.current = redirect;
    onErrorRef.current = onError;
    onProcessingChangeRef.current = onProcessingChange;
  }, [
    onError,
    onPaymentConfirmed,
    onProcessingChange,
    redirect,
    updateIntentHandle,
    userData,
  ]);

  useEffect(() => {
    if (
      !stripe ||
      !elements ||
      !clientSecret ||
      totalAmount == null ||
      totalAmount === ''
    ) {
      return;
    }

    // Prevent stale async work from previous totals from re-mounting an old button.
    const runId = ++effectRunIdRef.current;
    let cancelled = false;

    const amountNum = Number(totalAmount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      console.error('Invalid total amount');
      return;
    }

    // Tear down any existing button immediately so we can't click a stale one.
    if (prButtonRef.current) {
      try {
        prButtonRef.current.destroy();
      } catch (_) {}
      prButtonRef.current = null;
    }
    const mountEl = document.getElementById('payment-request-button');
    if (mountEl) {
      mountEl.innerHTML = '';
    }

    // Stripe Payment Request (Apple/Google/Link Pay) expects amount in smallest currency unit (cents for EUR).
    // totalAmount from parent is in euros (donation + tips + goodies), so convert to cents.
    const amountInCents = Math.round(amountNum * 100);

    const newPaymentRequest = stripe.paymentRequest({
      country: 'DE',
      currency: 'eur',
      total: { label: 'Total', amount: amountInCents },
      // The checkout form already collects the payer's details. Asking Apple Pay
      // for the name exposes the wallet's full name even when a supporter chose
      // initials or an anonymous display name.
      requestPayerName: false,
      requestPayerEmail: true
    });

    newPaymentRequest.canMakePayment().then((result) => {

      console.log(result, "REQ")

      if (cancelled || effectRunIdRef.current !== runId) return;
      if (result) {
        if (prButtonRef.current) {
          prButtonRef.current.destroy();
        }

        const prButton = elements.create('paymentRequestButton', {
          paymentRequest: newPaymentRequest,
          style: { paymentRequestButton: { type: 'buy' } },
        });

        // Guard in case the component was updated/unmounted mid-flight.
        if (!cancelled && effectRunIdRef.current === runId) {
          prButton.mount('#payment-request-button');
        }
        prButtonRef.current = prButton;

        newPaymentRequest.on('paymentmethod', async (ev) => {
          let walletSheetCompleted = false;
          const completeWalletSheet = (status) => {
            if (!walletSheetCompleted) {
              ev.complete(status);
              walletSheetCompleted = true;
            }
          };

          onProcessingChangeRef.current?.(true);

          try {
            // Persist the latest personalization before confirming. Otherwise a
            // quick wallet confirmation can race the metadata update and the
            // webhook creates a donation with stale or incomplete information.
            if (updateIntentRef.current) {
              await updateIntentRef.current(
                userDataRef.current?.anonymous || false
              );
            }

            const { paymentIntent, error: confirmError } =
              await stripe.confirmCardPayment(
                clientSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false }
              );

            if (confirmError) {
              throw confirmError;
            }

            completeWalletSheet('success');
            let confirmedPaymentIntent = paymentIntent;

            if (confirmedPaymentIntent?.status === 'requires_action') {
              const {
                paymentIntent: actionPaymentIntent,
                error,
              } = await stripe.confirmCardPayment(clientSecret);
              if (error) {
                throw error;
              }
              confirmedPaymentIntent =
                actionPaymentIntent || confirmedPaymentIntent;
            }

            if (!confirmedPaymentIntent?.id) {
              throw new Error('Stripe returned no confirmed PaymentIntent');
            }

            localStorage.removeItem('goodies');

            if (onPaymentConfirmedRef.current) {
              await onPaymentConfirmedRef.current(
                confirmedPaymentIntent,
                userDataRef.current
              );
            } else {
              redirectRef.current();
            }
          } catch (error) {
            completeWalletSheet('fail');
            onProcessingChangeRef.current?.(false);
            onErrorRef.current?.(error);
          }
        });
      } else {
        if (document.getElementById('payment-request-button')) {
          document.getElementById('payment-request-button').style.display =
            'none';
        }
      }
    });

    return () => {
      cancelled = true;
      if (prButtonRef.current) {
        prButtonRef.current.destroy();
        prButtonRef.current = null;
      }
    };
  }, [
    stripe,
    elements,
    totalAmount,
    clientSecret,
  ]);
  return (
    <div>
      <div id="payment-request-button"></div>
    </div>
  );
};

export default StripeApplePay;
